/*
 * SQL — run once against your Supabase project before deploying:
 *
 *   ALTER TABLE profiles
 *     ADD COLUMN IF NOT EXISTS kit_requested BOOLEAN NOT NULL DEFAULT false,
 *     ADD COLUMN IF NOT EXISTS call_booked   BOOLEAN NOT NULL DEFAULT false;
 *
 * These columns track whether the retailer has confirmed kit shipment and
 * booked their onboarding call, completing steps 3 and 4 of the setup flow.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAppContext } from "@/contexts/AppContext";

const SB_URL   = import.meta.env.VITE_SUPABASE_URL   as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const EMBER = "#FF6B35";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface StepState {
  hasLocation:  boolean;
  hasRequests:  boolean;
  kitRequested: boolean;
  callBooked:   boolean;
}

interface OnboardingBannerProps {
  userId: string;
  jwt:    string;
  onDismiss: () => void;
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function OnboardingBanner({ userId, jwt, onDismiss }: OnboardingBannerProps) {
  const { impersonating } = useImpersonation();
  const { isDemo }        = useAppContext();
  // Match sticky offset to full header height: nav only (top-14) vs nav + top banner (top-24)
  const stickyTop = (impersonating || isDemo) ? "top-24" : "top-14";

  const DISMISS_KEY = `fyrris:ob:dismissed:${userId}`;

  /* Dismiss is stored in localStorage — persists for this browser.
   * It resets when the user clears storage or the column flips to true
   * (at which point PortalLayout stops rendering this component entirely). */
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const [steps, setSteps] = useState<StepState>({
    hasLocation:  false,
    hasRequests:  false,
    kitRequested: false,
    callBooked:   false,
  });
  const [stepsLoaded, setStepsLoaded] = useState(false);

  /* ── Fetch step completion ──────────────────────────────────── */
  useEffect(() => {
    if (dismissed || !userId || !jwt) return;

    let cancelled = false;
    (async () => {
      try {
        /* 1 — Retailer row for this user */
        const rRes = await fetch(
          `${SB_URL}/rest/v1/retailers?owner_user_id=eq.${userId}&select=id&limit=1`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
        );
        const [retailer]: [{ id: string }?] = rRes.ok ? await rRes.json() : [];
        const retailerId = retailer?.id;

        /* 2 — Parallel: locations, requests, profile extras */
        const [locRes, reqRes, profRes] = await Promise.all([
          retailerId
            ? fetch(
                `${SB_URL}/rest/v1/locations?retailer_id=eq.${retailerId}&select=id&limit=1`,
                { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
              )
            : Promise.resolve(null),
          retailerId
            ? fetch(
                `${SB_URL}/rest/v1/retailer_manufacturer_requests?retailer_id=eq.${retailerId}&select=id&limit=1`,
                { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
              )
            : Promise.resolve(null),
          fetch(
            `${SB_URL}/rest/v1/profiles?id=eq.${userId}&select=kit_requested,call_booked&limit=1`,
            { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
          ),
        ]);

        if (cancelled) return;

        const locs: unknown[]  = locRes?.ok  ? await locRes.json()  : [];
        const reqs: unknown[]  = reqRes?.ok  ? await reqRes.json()  : [];
        const [prof]: [{ kit_requested?: boolean; call_booked?: boolean }?] =
          profRes.ok ? await profRes.json() : [{}];

        setSteps({
          hasLocation:  locs.length > 0,
          hasRequests:  reqs.length > 0,
          kitRequested: prof?.kit_requested === true,
          callBooked:   prof?.call_booked   === true,
        });
      } catch {
        /* Non-fatal — banner just shows 0/4 */
      } finally {
        if (!cancelled) setStepsLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, jwt, dismissed, DISMISS_KEY]);

  /* ── Dismiss handler ────────────────────────────────────────── */
  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
    onDismiss();
  };

  if (dismissed) return null;

  /* ── Steps data ─────────────────────────────────────────────── */
  const stepList = [
    { label: "Location added",     done: steps.hasLocation  },
    { label: "Products requested", done: steps.hasRequests  },
    { label: "Kit confirmed",      done: steps.kitRequested },
    { label: "Call booked",        done: steps.callBooked   },
  ];
  const completedCount = stepList.filter((s) => s.done).length;

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div
      className={`sticky ${stickyTop} z-[999] w-full`}
      style={{ backgroundColor: EMBER }}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-6 sm:px-4">

        {/* Left — headline + subtext */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight">
            Complete your setup to go live 🚀
          </p>
          <p className="mt-0.5 text-xs text-gray-800 leading-snug">
            Takes 5 minutes — finish setting up your store to activate your AR catalogue
          </p>
        </div>

        {/* Right — progress + CTA */}
        <div className="flex flex-shrink-0 items-center gap-4">
          {/* Progress dots + count */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              {stepList.map((step, i) =>
                stepsLoaded ? (
                  <span
                    key={i}
                    title={step.label}
                    className={`block h-2.5 w-2.5 rounded-full transition ${
                      step.done
                        ? "bg-white"
                        : "bg-transparent border-2 border-white/60"
                    }`}
                  />
                ) : (
                  <span
                    key={i}
                    className="block h-2.5 w-2.5 rounded-full bg-white/30 animate-pulse"
                  />
                ),
              )}
            </div>
            <span className="text-xs font-medium text-gray-900 whitespace-nowrap">
              {stepsLoaded ? `${completedCount} of 4 steps complete` : "Loading…"}
            </span>
          </div>

          {/* CTA */}
          <Link
            to="/portal/onboarding"
            className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white whitespace-nowrap hover:bg-gray-800 transition"
          >
            Continue Setup →
          </Link>
        </div>
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss setup banner"
        className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-gray-900/60 hover:bg-black/10 hover:text-gray-900 transition"
      >
        ✕
      </button>
    </div>
  );
}
